import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ViConfirmObject } from '@vi-iot/ui';

import { GTMService, UIService } from '@services';
import { RemoteDiagnosticsStore } from '@store';
import { InstallationService } from '../../services/installation.service';

@Component({
  selector: 'app-diagnostics-close-button',
  templateUrl: './diagnostics-close-button.component.html',
  styleUrls: ['./diagnostics-close-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsCloseButtonComponent implements OnInit, OnDestroy {
  @Input() installationId: number;
  @Input() gatewaySerial: string;

  viConfirmObject: ViConfirmObject;

  private isActiveSessionAvailable: boolean;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private installationService: InstallationService,
    private uiService: UIService,
    private gtmService: GTMService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.initViConfirmObject();
    this.remoteDiagnosticsStore
      .isActiveSessionAvailable(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isActiveSessionAvailable) => {
        this.viConfirmObject.displayDialog = isActiveSessionAvailable;
        this.isActiveSessionAvailable = isActiveSessionAvailable;
      });
  }

  confirm(): void {
    this.remoteDiagnosticsStore.triggerDisconnectSequence(this.gatewaySerial);
    this.closeDiagnosticOverlay();

    this.gtmService.track(
      'onConfirm',
      'Close overlay and disconnect remote diagnostics',
      'diagnosticsPage'
    );
  }

  cancel(): void {
    this.gtmService.track(
      'onCancel',
      'Abort closing overlay and disconnecting  remote diagnostics',
      'diagnosticsPage'
    );
  }

  close(): void {
    if (!this.isActiveSessionAvailable) {
      this.closeDiagnosticOverlay();
      this.gtmService.track('onClose', 'Close remote diagnostics overlay', 'diagnosticsPage');
    }
  }

  closeDiagnosticOverlay(): void {
    this.installationService.switchSelectionToMainDevice();
    this.uiService.closeActiveOverlay(this.gatewaySerial, this.installationId, true);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initViConfirmObject(): void {
    this.viConfirmObject = {
      title: this.translateService.instant('DIAGNOSTICS.DISCONNECT_MODAL.DISCONNECT_TITLE'),
      content: this.translateService.instant('DIAGNOSTICS.DISCONNECT_MODAL.DISCONNECT_TEXT'),
      cancelBtn: this.translateService.instant('GENERAL.BUTTON.NO'),
      confirmBtn: this.translateService.instant('GENERAL.BUTTON.YES'),
      displayDialog: false,
    };
  }
}
